import { useEffect, useState, type ReactElement } from 'react';
import { IconButton } from '../primitives/index.js';
import { Icon } from '../icons/index.js';

type Theme = 'system' | 'light' | 'dark';
const STORAGE_KEY = 'erm.theme';

function read(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage blocked — system preference still applies.
  }
  return 'system';
}

/**
 * Three states. `system` stamps NOTHING on the root, so the media query in
 * tokens.css governs; an explicit choice stamps `data-theme` and wins in both
 * directions.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function ThemeToggle(): ReactElement {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => {
    applyTheme(theme);
    try {
      if (theme === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Nothing to persist.
    }
  }, [theme]);

  const next: Theme = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
  const label = `Theme: ${theme}. Switch to ${next}.`;

  // A drawing, not a Unicode circle. Three half-filled glyphs standing in for
  // sun, moon and "follow the system" is the sort of placeholder the icon
  // system exists to remove: they render differently on every platform, they
  // carry no stroke weight in common with anything else on the page, and none
  // of them means what it is being asked to mean.
  const glyph = theme === 'dark' ? 'themeDark' : theme === 'light' ? 'themeLight' : 'themeSystem';

  return (
    <IconButton label={label} onClick={() => setTheme(next)}>
      <Icon name={glyph} size="sm" />
    </IconButton>
  );
}
